"use client";

import { PhoneInputE164 } from "@/components/PhoneInputE164";
import type { FormEvent } from "react";

/** The audited copy the compact card shows (from ValueMomentContactCapture). */
type ValueMomentCopy = Record<
  | "heading"
  | "body"
  | "cta"
  | "saving"
  | "skip"
  | "placeholder"
  | "countryLabel"
  | "invalid"
  | "needsPhone"
  | "error"
  | "success",
  string
>;

/** State and actions from ValueMomentContactCapture's shared opt-in hook. Typed
 * here, not imported, so this module does not import its parent (no cycle). */
type ValueMomentOptIn = {
  state: "idle" | "saving" | "saved" | "error" | "invalid";
  needsPhone: boolean;
  asksForPhone: boolean;
  phoneInput: string;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
  changePhone: (next: string) => void;
  dismiss: () => void;
};

/**
 * Reserved height for the compact card (CWV / CLS). The card sits above the
 * reading, so every state it can reach after a tap — saving, confirmed, the
 * phone input for a reading with no known phone, an error line — renders
 * inside this box: the action row is always one `min-h-11` row and the status
 * line is always present. Sized for the longer Hindi copy at 360px wide.
 *
 * Measured 2026-10-27 in Chromium on the rashi result (Playwright, en + hi):
 * phone 161.5px in every state (idle, phone needed, confirmed); desktop 112px
 * idle but 128px once the phone input or the confirmation shows, because the
 * action column wraps. The old `sm:min-h-28` (112px) let the card grow 16px
 * after a tap, pushing everything below it; `sm:min-h-32` (128px) holds them.
 */
export const COMPACT_CAPTURE_RESERVE = "min-h-40 sm:min-h-32";

/**
 * Compact layout of the value-moment opt-in (the kundali-result card under the
 * chart). Same audited heading and body as the full card, verbatim — fewer
 * words would change what the recorded copy version says the seeker agreed to.
 * Only the layout is denser: copy and action share one row from `sm`.
 */
export function CompactValueMomentCapture({
  copy,
  optIn,
  targetId,
}: {
  copy: ValueMomentCopy;
  optIn: ValueMomentOptIn;
  targetId: string;
}) {
  const { asksForPhone, needsPhone, phoneInput, state } = optIn;
  const saved = state === "saved";
  const failed = state === "invalid" || state === "error";
  let statusLine = needsPhone ? copy.needsPhone : "";
  if (state === "invalid") statusLine = copy.invalid;
  if (state === "error") statusLine = copy.error;
  return (
    <div
      className={`flex flex-col justify-center gap-2 rounded-lg border border-border-subtle bg-surface-warm p-3 sm:flex-row sm:items-center sm:gap-4 ${COMPACT_CAPTURE_RESERVE}`}
      data-testid="value-moment-contact-capture"
      data-variant="compact"
    >
      <div className="min-w-0 flex-1">
        <p className="text-body-small font-semibold text-text-primary">{copy.heading}</p>
        <p className="text-caption leading-snug text-text-muted">{copy.body}</p>
      </div>
      <div className="flex flex-col sm:max-w-xs sm:shrink-0">
        {saved ? (
          <p
            role="status"
            className="flex min-h-11 items-center text-body-small font-medium text-intent-success"
          >
            {copy.success}
          </p>
        ) : (
          <form
            translate="no"
            onSubmit={optIn.handleSubmit}
            className="flex min-h-11 items-center gap-2"
          >
            {asksForPhone ? (
              <PhoneInputE164
                value={phoneInput}
                onChange={optIn.changePhone}
                disabled={state === "saving"}
                id={`vm-compact-phone-${targetId}`}
                name="phone"
                placeholder={copy.placeholder}
                countryLabel={copy.countryLabel}
                wrapperClassName="flex min-h-11 min-w-0 flex-1 items-stretch"
                className="min-w-0 flex-1 rounded-r px-2 py-2 text-body-small outline-none"
              />
            ) : null}
            <button
              type="submit"
              disabled={state === "saving" || (asksForPhone && !phoneInput.trim())}
              className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-lg bg-surface-brand-strong px-4 text-body-small font-semibold text-text-on-brand transition hover:bg-surface-brand-strong-hover disabled:opacity-50"
            >
              {state === "saving" ? copy.saving : copy.cta}
            </button>
            <button
              type="button"
              onClick={optIn.dismiss}
              className="inline-flex min-h-11 shrink-0 items-center px-2 text-caption font-medium text-text-muted"
            >
              {copy.skip}
            </button>
          </form>
        )}
        <p
          aria-live="polite"
          className={`min-h-4 text-caption ${failed ? "text-intent-error" : "text-text-secondary"}`}
        >
          {saved ? "" : statusLine}
        </p>
      </div>
    </div>
  );
}
